/**
 * Acquisition, Performance Overview figures, sourced from the Owners Hub Stats API.
 *
 * Wires the KPI grid's TWO columns to `state.ownerHubStats`, harmonised with the Home
 * dashboard's range selector + window logic:
 *  - LEFT  = the selected range. For the live "Today" range the current call is a
 *            single-day window + a SEPARATE prior-day call; every other range issues
 *            ONE extended window covering the prior period too, split client-side,
 *            exactly like `useHomeStats`.
 *  - RIGHT = the "next step up" rolling window ({@link RIGHT_STEP}): current + prior
 *            calls. The right window always includes today, so it stays live.
 *
 * The figures come from `daily_affiliate_activity` grouped on `aft`, and only the
 * acquisition-channel groups ({@link ACQUISITION_AFTS}) are summed, so the totals count
 * only marketing-acquisition records (the same six channels the Home Acquisition
 * Channels grid shows). Using `daily_client_activity` here would over-count: it includes
 * organic / direct / untracked players that have no `aft`.
 *
 * Both columns carry a period-over-period delta (the API has no comparison field) and
 * re-key together when the selector changes. Every live window re-polls ~every 5
 * minutes (stale-while-revalidate). See `pages/home/useHomeStats.ts` + `pages/home/useHomeChannels.ts`
 * + `docs/api.owners-hub.stats.md`.
 *
 * Three KPIs (Budget spent, Blended CPA, ROI) depend on marketing ad-spend, which no
 * stats data set carries; they are sourced from the Casino Ad Service campaign cost
 * via {@link useAcquisitionAdSpend} + {@link buildAdSpendStat} (CPA / ROI also use the
 * stats FTDs / GGR already fetched here). See `missing-data.md`.
 */
import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchStatsDataSetHistory,
  selectStatsDataSetHistory,
  selectStatsDataSetHistoryError,
  selectStatsDataSetHistoryLoading,
  type StatsHistoryEntry,
  type StatsHistoryRef,
} from '@/store/slices/ownerHubStats';

import type { HomePeriod, Kpi } from '../../mock-data/types';
import { homeWindow, intervalIncludesToday, shiftWindow } from '../home/stats-mapping';
import { type Bucket, liveBuckets, matchedBaseline, resolvePeriods, toBucketsForAfts } from '../home/stats-shape';
import { LIVE_REFRESH_MS, useRefreshTick } from '../home/useRefreshTick';
import {
  ACQUISITION_AFTS,
  type AdSpendColumnInput,
  buildAdSpendStat,
  buildPerformanceKpis,
  type ColumnInput,
  isAdSpendKpiId,
  rightStepOf,
  rollingWindow,
} from './performance-stats';
import { useAcquisitionAdSpend, type AdSpendWindows } from './useAcquisitionAdSpend';

/** The date part (`YYYY-MM-DD`) of a window date that may be an ISO datetime — the
 *  TODAY / YESTERDAY windows are UTC-day instants (`2026-06-26T00:00:00.000Z`), the
 *  multi-day ranges are already plain dates. The ad-service / ExoClick reject an ISO
 *  datetime ("must be Y-m-d"), so every `date_from` / `date_to` is normalised through this. */
const toYmd = (date: string): string => date.slice(0, 10);

/** The inclusive last day (YYYY-MM-DD) of an end-exclusive stats window end date. */
const inclusiveEnd = (endDateExclusive: string): string => {
  const [y, m, d] = toYmd(endDateExclusive).split('-').map(Number);
  const prev = new Date(Date.UTC(y, m - 1, d) - 86_400_000);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${prev.getUTCFullYear()}-${pad(prev.getUTCMonth() + 1)}-${pad(prev.getUTCDate())}`;
};

/** Affiliate-attributed activity, grouped on `aft` so only acquisition channels count. */
const DATA_SET = 'daily_affiliate_activity';
const AGGREGATE_ON = ['aft'];
const DIMENSION = 'aft';

/** Sum the acquisition-channel groups of a history payload into per-date buckets. */
const acqBuckets = (entry: StatsHistoryEntry | undefined): Bucket[] =>
  entry ? toBucketsForAfts(entry.data, DIMENSION, ACQUISITION_AFTS) : [];

/**
 * Stale-while-revalidate loading: the skeleton shows ONLY when there is no cached
 * history yet. A background refresh (incl. the 5-min live re-poll) keeps the cached
 * figures on screen; a first load that fails stops loading (falls through to `N/A`);
 * a missing brand reads as loading. Mirrors `useHomeStats`'s `sectionLoading`.
 */
const sectionLoading = (
  hasBrand: boolean,
  loadingFlag: boolean,
  entry: StatsHistoryEntry | undefined,
  error: string | undefined,
): boolean => !hasBrand || (entry === undefined && (loadingFlag || error === undefined));

const histRef = (
  casinoInstanceId: string | undefined,
  startDate: string,
  endDate: string,
  intervalSlice: string,
): StatsHistoryRef | undefined =>
  casinoInstanceId
    ? { casinoInstanceId, dataSet: DATA_SET, startDate, endDate, intervalSlice, aggregateOn: AGGREGATE_ON }
    : undefined;

export type AcquisitionPerformanceResult = { kpis: Kpi[]; loading: boolean };

/**
 * @param casinoInstanceId Active brand id (`displayBrand?.id`); `undefined` keeps the
 *        section loading until a brand resolves.
 * @param tenantId Active brand tenant id (`displayBrand?.tenant_id`), for the
 *        ad-service spend behind the three ad-spend KPIs.
 * @param mockKpis The mock Performance KPIs, supplying the static chrome + the figures
 *        for any KPI without an API spec.
 * @param interval The selected range (defaults to the live TODAY day).
 */
export const useAcquisitionPerformance = (
  casinoInstanceId: string | undefined,
  tenantId: string | undefined,
  mockKpis: Kpi[],
  interval: HomePeriod = 'TODAY',
): AcquisitionPerformanceResult => {
  const dispatch = useAppDispatch();

  // The right window always includes today, so it always re-polls; the left re-polls
  // only when its window includes today (all ranges but Yesterday). The tick is also a
  // window dep so the windows roll over at UTC/local midnight (the clock read is a
  // hidden time dep).
  const live = interval === 'TODAY';
  const includesToday = intervalIncludesToday(interval);
  const tick = useRefreshTick(true, LIVE_REFRESH_MS);
  const leftTick = includesToday ? tick : 0;

  /* ----- LEFT column: the selected range (mirrors useHomeStats) ----- */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const window = React.useMemo(() => homeWindow(interval, Date.now()), [interval, leftTick]);
  const priorWindow = React.useMemo(() => shiftWindow(window, window.spanDays), [window]);
  // Today: just the current window (+ a separate prior call). Other ranges: ONE
  // extended window covering both periods (split client-side).
  const leftMainWindow = live
    ? { startDate: window.startDate, endDate: window.endDate, intervalSlice: window.intervalSlice }
    : { startDate: priorWindow.startDate, endDate: window.endDate, intervalSlice: window.intervalSlice };

  const leftMainRef = histRef(
    casinoInstanceId,
    leftMainWindow.startDate,
    leftMainWindow.endDate,
    leftMainWindow.intervalSlice,
  );
  const leftPriorRef = live
    ? histRef(casinoInstanceId, priorWindow.startDate, priorWindow.endDate, window.intervalSlice)
    : undefined;

  const leftMainEntry = useAppSelector((s) => selectStatsDataSetHistory(s, leftMainRef));
  const leftMainLoading = useAppSelector((s) => selectStatsDataSetHistoryLoading(s, leftMainRef));
  const leftMainError = useAppSelector((s) => selectStatsDataSetHistoryError(s, leftMainRef));
  const leftPriorEntry = useAppSelector((s) => selectStatsDataSetHistory(s, leftPriorRef));

  /* ----- RIGHT column: the "next step up" rolling window ----- */
  const step = rightStepOf(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rightWindow = React.useMemo(() => rollingWindow(step.spanDays, Date.now()), [step.spanDays, tick]);
  const rightPriorWindow = React.useMemo(() => shiftWindow(rightWindow, step.spanDays), [rightWindow, step.spanDays]);

  /* ----- ad-spend: the same four windows, queried against the ad-service cost ----- */
  // Inclusive end dates (the stats windows are end-exclusive, ending tomorrow).
  const adWindows = React.useMemo<AdSpendWindows>(
    () => ({
      leftCurrent: { dateFrom: toYmd(window.startDate), dateTo: inclusiveEnd(window.endDate) },
      leftBaseline: { dateFrom: toYmd(priorWindow.startDate), dateTo: inclusiveEnd(priorWindow.endDate) },
      rightCurrent: { dateFrom: toYmd(rightWindow.startDate), dateTo: inclusiveEnd(rightWindow.endDate) },
      rightBaseline: { dateFrom: toYmd(rightPriorWindow.startDate), dateTo: inclusiveEnd(rightPriorWindow.endDate) },
    }),
    [window, priorWindow, rightWindow, rightPriorWindow],
  );
  const adSpend = useAcquisitionAdSpend(tenantId, adWindows);

  const rightRef = histRef(casinoInstanceId, rightWindow.startDate, rightWindow.endDate, step.intervalSlice);
  const rightPriorRef = histRef(
    casinoInstanceId,
    rightPriorWindow.startDate,
    rightPriorWindow.endDate,
    step.intervalSlice,
  );

  const rightEntry = useAppSelector((s) => selectStatsDataSetHistory(s, rightRef));
  const rightLoadingFlag = useAppSelector((s) => selectStatsDataSetHistoryLoading(s, rightRef));
  const rightError = useAppSelector((s) => selectStatsDataSetHistoryError(s, rightRef));
  const rightPriorEntry = useAppSelector((s) => selectStatsDataSetHistory(s, rightPriorRef));

  /* ----- dispatch the calls (all grouped on aft) ----- */
  // Left main, re-dispatched on the left tick (only ticks when the range includes today).
  React.useEffect(() => {
    if (!casinoInstanceId) return;
    dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId,
        dataSet: DATA_SET,
        ...leftMainWindow,
        aggregateOn: [...AGGREGATE_ON],
      }),
    );
  }, [
    dispatch,
    casinoInstanceId,
    leftMainWindow.startDate,
    leftMainWindow.endDate,
    leftMainWindow.intervalSlice,
    leftTick,
  ]);

  // Left separate prior call, Today only.
  React.useEffect(() => {
    if (!casinoInstanceId || !live) return;
    dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId,
        dataSet: DATA_SET,
        startDate: priorWindow.startDate,
        endDate: priorWindow.endDate,
        intervalSlice: window.intervalSlice,
        aggregateOn: [...AGGREGATE_ON],
      }),
    );
  }, [dispatch, casinoInstanceId, live, priorWindow.startDate, priorWindow.endDate, window.intervalSlice]);

  // Right current, always includes today, so re-dispatched on the tick.
  React.useEffect(() => {
    if (!casinoInstanceId) return;
    dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId,
        dataSet: DATA_SET,
        startDate: rightWindow.startDate,
        endDate: rightWindow.endDate,
        intervalSlice: step.intervalSlice,
        aggregateOn: [...AGGREGATE_ON],
      }),
    );
  }, [dispatch, casinoInstanceId, rightWindow.startDate, rightWindow.endDate, step.intervalSlice, tick]);

  // Right prior, a final window (ends before the right window starts), so it never re-polls.
  React.useEffect(() => {
    if (!casinoInstanceId) return;
    dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId,
        dataSet: DATA_SET,
        startDate: rightPriorWindow.startDate,
        endDate: rightPriorWindow.endDate,
        intervalSlice: step.intervalSlice,
        aggregateOn: [...AGGREGATE_ON],
      }),
    );
  }, [dispatch, casinoInstanceId, rightPriorWindow.startDate, rightPriorWindow.endDate, step.intervalSlice]);

  const hasBrand = Boolean(casinoInstanceId);
  const now = Date.now();

  // LEFT, drop future buckets, then resolve current + prior (separate call for Today,
  // split otherwise) and count-match the baseline. Each window sums only the acquisition
  // channel groups (toBucketsForAfts), so the figure excludes organic / direct / untracked.
  const leftMainBuckets = liveBuckets(acqBuckets(leftMainEntry), now);
  const leftPriorBuckets = acqBuckets(leftPriorEntry);
  const leftPeriods = resolvePeriods(leftMainBuckets, leftPriorBuckets, live, Date.parse(window.startDate));
  const leftLoading = sectionLoading(hasBrand, leftMainLoading, leftMainEntry, leftMainError);
  const left: ColumnInput = {
    current: leftPeriods.current,
    baseline: matchedBaseline(leftPeriods.current, leftPeriods.previous),
    errored: !leftLoading && leftMainError !== undefined && leftMainEntry === undefined,
  };

  // RIGHT, current + prior as separate calls, count-matched.
  const rightCurrent = liveBuckets(acqBuckets(rightEntry), now);
  const rightPrior = acqBuckets(rightPriorEntry);
  const rightLoading = sectionLoading(hasBrand, rightLoadingFlag, rightEntry, rightError);
  const right: ColumnInput = {
    current: rightCurrent,
    baseline: matchedBaseline(rightCurrent, rightPrior),
    errored: !rightLoading && rightError !== undefined && rightEntry === undefined,
  };

  // The stats-backed KPIs build straight from the buckets; the three ad-spend KPIs
  // (still `{ kind: 'missing' }` in the specs) are then overridden with the real
  // spend cost + the FTDs / GGR from the same column buckets. Until the spend loads
  // they keep their `?`; on a spend failure they read `N/A`.
  const kpis = buildPerformanceKpis(mockKpis, left, right).map((kpi) => {
    if (!isAdSpendKpiId(kpi.id)) return kpi;
    const todayCol: AdSpendColumnInput = {
      cost: adSpend.left.cost,
      baselineCost: adSpend.left.baselineCost,
      current: left.current,
      baseline: left.baseline,
      errored: adSpend.errored,
    };
    const monthCol: AdSpendColumnInput = {
      cost: adSpend.right.cost,
      baselineCost: adSpend.right.baselineCost,
      current: right.current,
      baseline: right.baseline,
      errored: adSpend.errored,
    };
    return { ...kpi, today: buildAdSpendStat(kpi.id, todayCol), month: buildAdSpendStat(kpi.id, monthCol) };
  });
  return { kpis, loading: leftLoading || rightLoading };
};

export default useAcquisitionPerformance;
